import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, User, Phone, Mail, FileText, Wallet, Percent, TrendingUp } from 'lucide-react';
import { formatCurrency, formatDate } from '@/utils/helpers';
import { toast } from 'sonner';

function MemberProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.email) return;
      try {
        setLoading(true);
        // Fetch account holder by email
        const res = await api.get('/account-holders', {
          params: { search: user.email }
        });

        if (res.data && res.data.length > 0) {
          // Find the exact match or first one
          const match = res.data.find(p => p.email === user.email) || res.data[0];
          setProfile(match);
        }
      } catch (error) {
        console.error("Failed to fetch profile", error);
        toast.error("Failed to load profile details");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 fade-in max-w-4xl mx-auto">
        <div>
          <h1 className="text-3xl font-bold mb-2 text-gray-900" style={{ fontFamily: 'Crimson Text, serif' }}>
            My Profile
          </h1>
          <p className="text-gray-600">View your account details and financial standing.</p>
        </div>

        {profile ? (
          <div className="grid gap-6 md:grid-cols-2">
            {/* Profile Card */}
            <Card className="md:col-span-2 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-100">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
                  <div className="h-24 w-24 rounded-full bg-white shadow-md flex items-center justify-center text-3xl font-bold text-emerald-600">
                    {profile.first_name[0]}{profile.last_name[0]}
                  </div>
                  <div className="space-y-2 flex-1">
                    <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                      <h2 className="text-2xl font-bold text-gray-900">
                        {profile.first_name} {profile.middle_name} {profile.last_name}
                      </h2>
                      <Badge variant="outline" className="bg-white text-emerald-700 w-fit">
                        {profile.account_number}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Phone size={16} /> {profile.contact_number}
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail size={16} /> {profile.email}
                      </div>
                      <div className="flex items-center gap-2">
                        <CalendarIcon date={profile.join_date} /> Joined: {formatDate(profile.join_date)}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Profit Share Card - NEW */}
            <Card className="border-emerald-200 shadow-sm overflow-hidden">
              <div className="bg-emerald-600 p-4 flex items-center justify-between text-white">
                <div className="flex items-center gap-2">
                  <Percent className="h-5 w-5" />
                  <span className="font-semibold">Profit Share Balance</span>
                </div>
                <TrendingUp className="h-5 w-5 opacity-80" />
              </div>
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-gray-900">
                  {formatCurrency(profile.profit_share_balance || 0)}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Accumulated profit distributed from loan interest earnings.
                </p>
              </CardContent>
            </Card>

            {/* Other Stats Placeholders */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Wallet className="h-4 w-4" /> Account Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Lavajam Status</span>
                    <Badge variant={profile.opts_in_lavajam ? "success" : "secondary"}>
                      {profile.opts_in_lavajam ? "Active" : "Not Enrolled"}
                    </Badge>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Loan Status</span>
                    <Badge variant={profile.is_loan_restricted ? "destructive" : "outline"}>
                      {profile.is_loan_restricted ? "Restricted" : "Eligible"}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center text-gray-500">
              <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Profile information not found.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-calendar"><rect width="18" height="18" x="3" y="4" rx="2" ry="2" /><line x1="16" x2="16" y1="2" y2="6" /><line x1="8" x2="8" y1="2" y2="6" /><line x1="3" x2="21" y1="10" y2="10" /></svg>
)

export default MemberProfile;
